
    import __i18nConfig from '@next-translate-root/i18n'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    
import React from "react";
import Image from "next/image";
import useTranslation from "next-translate/useTranslation";
import Page from "@/src/components/Page";
import DocShell from "@/src/layouts/DocShell";
import DocNavItem from "@/src/components/docs/DocNavItem";
import H1 from "@/src/components/docs/H1";
import H2 from "@/src/components/docs/H2";
import CustomLink from "@/src/components/CustomLink";
import { IoRemoveOutline, IoEllipse } from "react-icons/io5";
import {
  Box,
  Flex,
  List,
  ListIcon,
  ListItem,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorMode,
} from "@chakra-ui/react";

const PrivacyPolicy = () => {
  const { t } = useTranslation("legal");
  const { colorMode } = useColorMode();
  const isLight = colorMode === "light";

  const bodyColor = isLight ? "inherit" : "white";
  const headingColor = isLight ? "inherit" : "brand.600";

  const list = (key) => t(key, null, { returnObjects: true }) || [];
  const apdpRef = t("apdpRef");
  const sections = list("privacy.sections");

  // Paragraphe courant
  const P = ({ children, ...rest }) => (
    <Text
      as={"p"}
      color={bodyColor}
      py={2}
      lineHeight={"1.6"}
      textAlign={"justify"}
      {...rest}
    >
      {children}
    </Text>
  );

  // Liste à puces — « • » ou « – » selon la section, comme dans le document
  const Bullets = ({ items, dash }) => (
    <List spacing={3} mt={4} ml={{ base: 3, lg: 6 }}>
      {items.map((item, i) => (
        <ListItem key={i} color={bodyColor} lineHeight={"1.6"} textAlign={"justify"}>
          <ListIcon
            as={dash ? IoRemoveOutline : IoEllipse}
            color={"brand.500"}
            boxSize={dash ? 4 : 2}
          />
          {item}
        </ListItem>
      ))}
    </List>
  );

  const Mail = ({ address }) => (
    <CustomLink to={`mailto:${address}`}>
      <Text as={"a"} color={"brand.500"}>
        {address}
      </Text>
    </CustomLink>
  );

  const Site = ({ href, label }) => (
    <CustomLink to={href}>
      <Text as={"a"} color={"brand.500"}>
        {label}
      </Text>
    </CustomLink>
  );

  const Article = ({ children }) => (
    <Box as={"article"} py={4} px={{ base: 2, md: 8, xl: 12 }}>
      {children}
    </Box>
  );

  return (
    <DocShell
      aside={
        <>
          {sections.map((title, i) => (
            <DocNavItem key={i} icon={IoRemoveOutline} to={String(i + 1)}>
              {title}
            </DocNavItem>
          ))}
        </>
      }
    >
      <Article>
        <H1 color={headingColor}>{t("privacy.title")}</H1>

        {/* Mention de l'autorisation délivrée par l'APDP */}
        <Flex
          align={"center"}
          gap={3}
          mt={4}
          mb={2}
          pl={"7px"}
          py={"7px"}
          pr={"18px"}
          w={"fit-content"}
          maxW={"100%"}
          borderWidth={"1px"}
          borderColor={isLight ? "gray.200" : "brand.700"}
          borderRadius={"999px"}
        >
          <Box position={"relative"} flexShrink={0} w={"44px"} h={"44px"}>
            <Image
              src={"/apdp-logo.png"}
              alt={t("apdpBadge.alt")}
              layout={"fill"}
              objectFit={"contain"}
            />
          </Box>
          <Text
            fontSize={"13px"}
            fontWeight={"semibold"}
            lineHeight={"1.4"}
            color={isLight ? "blackAlpha.700" : "whiteAlpha.800"}
          >
            {t("apdpBadge.label")}
          </Text>
        </Flex>

        <H2 color={bodyColor} py={2}>
          {apdpRef}
        </H2>

        <P>{t("privacy.intro1")}</P>
        <P>
          {t("privacy.intro2a")} <b>Watchman</b>
          {t("privacy.intro2b")}
        </P>
        <P>{t("privacy.intro3")}</P>
      </Article>

      {/* ── 1 ─────────────────────────────────────────────────────────────── */}
      <Article>
        <H1 color={headingColor} id={"1"} py={4}>
          1. {sections[0]}
        </H1>
        <P>
          {t("privacy.s1.controller")}{" "}
          <b>Sètondji Bégis Pascal DAGBEHOU</b>.
        </P>
        <P>{t("privacy.s1.phone")}</P>
        <P>
          <b>{t("privacy.s1.emailLabel")}</b> :{" "}
          <Mail address={"begis@gits.bj"} />
        </P>
        <Bullets items={list("privacy.s1.list")} />
      </Article>

      {/* ── 2 ─────────────────────────────────────────────────────────────── */}
      <Article>
        <H1 color={headingColor} id={"2"} py={8}>
          2. {sections[1]}
        </H1>
        <P>
          {t("privacy.s2.p1a")} <b>{apdpRef}</b> {t("privacy.s2.p1b")}{" "}
          <Site href={"https://www.watchman.bj"} label={"www.watchman.bj"} />{" "}
          {t("privacy.s2.p1c")}
        </P>
        <P>{t("privacy.s2.contactIntro")}</P>
        <P>{t("privacy.s2.phone")}</P>
        <P>
          {t("privacy.s2.emailLabel")} <Mail address={"contact@apdp.bj"} /> ;
        </P>
        <P>{t("privacy.s2.address")}</P>
      </Article>

      {/* ── 3 ─────────────────────────────────────────────────────────────── */}
      <Article>
        <H1 color={headingColor} id={"3"} py={8}>
          3. {sections[2]}
        </H1>
        <P>
          {t("privacy.s3.p1a")}{" "}
          <Site href={"https://watchman.bj"} label={"https://watchman.bj"} />{" "}
          {t("privacy.s3.p1b")}
        </P>
        <P>{t("privacy.s3.p2")}</P>
      </Article>

      {/* ── 4 ─────────────────────────────────────────────────────────────── */}
      <Article>
        <H1 color={headingColor} id={"4"} py={8}>
          4. {sections[3]}
        </H1>
        <P>{t("privacy.s4.p1")}</P>
        <P>{t("privacy.s4.p2")}</P>
        <P>{t("privacy.s4.p3")}</P>
        <P>{t("privacy.s4.p4")}</P>
      </Article>

      {/* ── 5 ─────────────────────────────────────────────────────────────── */}
      <Article>
        <H1 color={headingColor} id={"5"} py={8}>
          5. {sections[4]}
        </H1>
        <P>{t("privacy.s5.intro")}</P>
        <Bullets items={list("privacy.s5.list")} dash />
        <P>{t("privacy.s5.footer")}</P>
      </Article>

      {/* ── 6 ─────────────────────────────────────────────────────────────── */}
      <Article>
        <H1 color={headingColor} id={"6"} py={8}>
          6. {sections[5]}
        </H1>
        <H2 color={bodyColor} py={2}>
          {t("privacy.s6.sub1")}
        </H2>
        <Bullets items={list("privacy.s6.list")} dash />
        <H2 color={bodyColor} pt={6} pb={2}>
          {t("privacy.s6.sub2")}
        </H2>
        <P>{t("privacy.s6.p")}</P>
      </Article>

      {/* ── 7 ─────────────────────────────────────────────────────────────── */}
      <Article>
        <H1 color={headingColor} id={"7"} py={8}>
          7. {sections[6]}
        </H1>
        <P>{t("privacy.s7.p1")}</P>
        <P>{t("privacy.s7.contactIntro")}</P>
        <P>
          <b>{t("privacy.s7.phoneLabel")}</b> : {t("privacy.s7.phone")}
        </P>
        <P>
          <b>{t("privacy.s7.emailLabel")}</b> :{" "}
          <Mail address={"support@watchman.bj"} />
        </P>
        <P>{t("privacy.s7.tableIntro")}</P>

        <Box overflowX={"auto"} mt={4}>
          <Table size={"sm"} variant={"simple"}>
            <Thead>
              <Tr>
                {list("privacy.s7.tableHeaders").map((header, i) => (
                  <Th key={i} color={bodyColor} whiteSpace={"normal"}>
                    {header}
                  </Th>
                ))}
              </Tr>
            </Thead>
            <Tbody>
              <Tr>
                <Td color={bodyColor} whiteSpace={"normal"}>
                  {t("privacy.s7.tableModality")}
                </Td>
                <Td color={bodyColor} whiteSpace={"normal"}>
                  <Text as={"span"} display={"block"} fontWeight={"semibold"}>
                    {t("privacy.s7.tableCountry")}
                  </Text>
                  <Text as={"span"} display={"block"}>
                    {t("privacy.s7.tableDetail")}
                  </Text>
                </Td>
              </Tr>
            </Tbody>
          </Table>
        </Box>

        <P>{t("privacy.s7.footer")}</P>
      </Article>

      {/* ── 8 ─────────────────────────────────────────────────────────────── */}
      <Article>
        <H1 color={headingColor} id={"8"} py={8}>
          8. {sections[7]}
        </H1>
        <P>{t("privacy.s8.intro")}</P>
        {list("privacy.s8.list").map((droit, i) => (
          <P key={i} py={1}>
            {droit}
          </P>
        ))}
        <P>
          {t("privacy.s8.linkIntro")}{" "}
          <Site
            href={"https://service.apdp.bj/"}
            label={"https://service.apdp.bj/"}
          />
          .
        </P>
        <P>{t("privacy.s8.contactIntro")}</P>
        <P>{t("privacy.s8.address")}</P>
        <P>
          {t("privacy.s8.emailLabel")} <Mail address={"support@watchman.bj"} />
        </P>
        <P>{t("privacy.s8.footer")}</P>
      </Article>

      {/* ── 9 ─────────────────────────────────────────────────────────────── */}
      <Article>
        <H1 color={headingColor} id={"9"} py={8}>
          9. {sections[8]}
        </H1>
        <P>{t("privacy.s9.intro")}</P>
        <Bullets
          dash
          items={[
            t("privacy.s9.address"),
            <React.Fragment key={"plainte-mail"}>
              {t("privacy.s9.emailLabel")} <Mail address={"contact@apdp.bj"} />
            </React.Fragment>,
          ]}
        />
        <P>{t("privacy.s9.footer")}</P>
      </Article>

      {/* ── 10 ────────────────────────────────────────────────────────────── */}
      <Article>
        <H1 color={headingColor} id={"10"} py={8}>
          10. {sections[9]}
        </H1>
        <P>{t("privacy.s10.intro")}</P>
        <Bullets
          dash
          items={[
            <React.Fragment key={"savoir-mail"}>
              {t("privacy.s10.emailLabel")}{" "}
              <Mail address={"support@watchman.bj"} />
            </React.Fragment>,
            t("privacy.s10.post"),
            t("privacy.s10.phone"),
          ]}
        />
      </Article>
    </DocShell>
  );
};

const PrivacyPolicyPage = () => (
  <Page name="Privacy Policy" path="/docs/privacy-policy">
    <PrivacyPolicy />
  </Page>
);

export default PrivacyPolicyPage;


    export async function getStaticProps(ctx) {
        
        
        return {
          
          
          props: {
            
            ...(await __loadNamespaces({
              ...ctx,
              pathname: '/docs/privacy-policy',
              loaderName: 'getStaticProps',
              ...__i18nConfig,
              loadLocaleFrom: (l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default),
            }))
          }
        }
    }
  